import { useForm } from '@conform-to/react'
import { parse } from '@conform-to/zod'
import { type ActionFunctionArgs, json } from '@remix-run/node'
import { useFetcher, useFetchers, useMatch } from '@remix-run/react'
import { z } from 'zod'
import { ErrorList } from '#app/components/forms/error-list'
import { LaptopIcon, MoonIcon, SunIcon } from '#app/components/icons'
import { Button, type ButtonProps } from '#app/components/ui/button'
import { Tooltip } from '#app/components/ui/tooltip'
import { useHints } from '#app/hooks/useHints'
import { useRequestInfo } from '#app/hooks/useRequestInfo'
import { setTheme } from '#app/utils/theme.server'

const ThemeFormSchema = z.object({
	theme: z.enum(['light', 'dark']),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parse(formData, { schema: ThemeFormSchema })

	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}

	if (!submission.value) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}

	const { theme } = submission.value

	const responseInit = {
		headers: { 'set-cookie': setTheme(theme) },
	}

	return json({ success: true, submission }, responseInit)
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
	const hints = useHints()
	const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	const marketing = useMatch('/')

	if (optimisticMode) {
		return optimisticMode
	}

	return marketing ? 'light' : requestInfo.userPrefs.theme ?? hints.theme
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
	const fetchers = useFetchers()
	const themeFetcher = fetchers.find(f => f.formAction === '/resources/theme')

	if (themeFetcher && themeFetcher.formData) {
		const submission = parse(themeFetcher.formData, {
			schema: ThemeFormSchema,
		})

		return submission.value?.theme
	}
}

export function ThemeSwitch({ buttonProps }: { buttonProps?: ButtonProps }) {
	const fetcher = useFetcher<typeof action>()
	const theme = useTheme()

	const [form] = useForm({
		id: 'theme-switch',
		lastSubmission: fetcher.data?.submission,
	})

	const optimisticMode = useOptimisticThemeMode()
	const mode = optimisticMode ?? theme ?? 'light'
	const nextMode = mode === 'dark' ? 'light' : 'dark'

	const modeLabel = {
		light: <SunIcon />,
		dark: <MoonIcon />,
		system: <LaptopIcon />,
	}

	return (
		<fetcher.Form method="POST" action="/resources/theme" {...form.props}>
			<input type="hidden" name="theme" value={nextMode} />
			<div className="flex gap-2">
				<Tooltip text="Color mode">
					<Button
						size="icon-sm"
						type="submit"
						disabled={['submitting', 'loading'].includes(fetcher.state)}
						{...buttonProps}
					>
						{modeLabel[mode]}
					</Button>
				</Tooltip>
			</div>
			<ErrorList errors={form.errors} id={form.errorId} />
		</fetcher.Form>
	)
}
